import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { IntlProvider, MissingTranslationError } from 'react-intl';
import { useAuth } from '@auth';
import { useLocale } from '@/shared/localization/useLocale';
import enMessages from '@/shared/i18n/messages/en.json';
import ptBRMessages from '@/shared/i18n/messages/pt-BR.json';
import esMessages from '@/shared/i18n/messages/es.json';

type Messages = Record<string, string>;

const messagesMap: Partial<Record<string, Messages>> = {
  en: enMessages,
  'en-US': enMessages,
  'pt-BR': ptBRMessages,
  pt: ptBRMessages,
  es: esMessages,
  'es-MX': esMessages,
  'es-419': esMessages,
};

function getMessages(locale: string): Messages {
  const baseLocale = locale.split('-')[0];
  return messagesMap[locale] ?? (baseLocale ? messagesMap[baseLocale] : undefined) ?? enMessages;
}

interface Props {
  children: ReactNode;
}

export function AppIntlProvider({ children }: Props) {
  const fallbackLocale = useLocale();
  const { user } = useAuth();
  const locale = user?.locale ?? fallbackLocale;
  const messages = useMemo(() => getMessages(locale), [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const handleError = useCallback((err: unknown) => {
    if (err instanceof MissingTranslationError) return;
    throw err;
  }, []);

  return (
    <IntlProvider locale={locale} messages={messages} defaultLocale="en" onError={handleError}>
      {children}
    </IntlProvider>
  );
}
